'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import EnrollmentModal from '@/components/EnrollmentModal';
import EnrollmentForm from '@/components/EnrollmentForm';

export default function InternshipHero() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleEnrollClick = () => {
    if (!isHydrated) return;
    setIsModalOpen(true);
  };

  return (
    <section className="relative bg-gradient-to-br from-primary/5 via-sky-light/10 to-accent/5 pt-24 pb-16 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full border-2 border-green-500/30">
              <Icon name="SparklesIcon" size={20} variant="solid" className="text-green-600" />
              <span className="text-sm font-bold text-green-600 uppercase tracking-wide">100% FREE with Course Enrollment</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold text-foreground leading-tight">
              Real-World Internship Program
              <span className="block mt-2 bg-gradient-to-r from-primary via-accent to-sky-light bg-clip-text text-transparent">
                Build. Learn. Grow.
              </span>
            </h1>

            <p className="text-lg text-muted-foreground font-body">
              Get hands-on experience with production-level codebases and real industry projects. 
              <span className="font-semibold text-foreground"> FREE when you enroll in any course!</span>
            </p>

            <div className="flex flex-wrap gap-6 pt-4">
              <div className="flex items-center space-x-2">
                <Icon name="ClockIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">Flexible Duration</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CodeBracketIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">Real Projects</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="AcademicCapIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">Live Training Programs</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="UserGroupIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">Expert Mentorship</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="DocumentTextIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">Certificate Included</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <button
                onClick={handleEnrollClick}
                className="px-8 py-4 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-lg font-headline font-semibold text-lg shadow-sky-md hover:shadow-sky-lg transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Get FREE Internship</span>
                <Icon name="ArrowRightIcon" size={20} variant="solid" />
              </button>
              <button
                onClick={() => isHydrated && window.scrollTo({ top: document.getElementById('pricing')?.offsetTop || 0, behavior: 'smooth' })}
                className="px-8 py-4 bg-card border-2 border-primary text-primary rounded-lg font-headline font-semibold text-lg hover:bg-primary/5 transition-all duration-300"
              >
                View Pricing Options
              </button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 pt-8 border-t border-border">
              <div>
                <div className="text-2xl font-headline font-bold text-primary">500+</div>
                <div className="text-sm text-muted-foreground">Interns Trained</div>
              </div>
              <div>
                <div className="text-2xl font-headline font-bold text-primary">3</div>
                <div className="text-sm text-muted-foreground">Projects Built</div>
              </div>
              <div>
                <div className="text-2xl font-headline font-bold text-primary">4.8★</div>
                <div className="text-sm text-muted-foreground">Program Rating</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-sky-lg">
              <AppImage
                src="/assets/images/internship-hero.png"
                alt="Students working on real-world projects during internship"
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
            </div>
            
            {/* Floating Badge */}
            <div className="absolute -top-4 -right-4 bg-gradient-to-br from-green-500 to-emerald-600 text-white px-6 py-3 rounded-full shadow-lg animate-float">
              <div className="text-2xl font-bold">FREE</div>
              <div className="text-xs">with Course</div>
            </div>
          </div>
        </div>
      </div>

      {isHydrated && (
        <EnrollmentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <EnrollmentForm onClose={() => setIsModalOpen(false)} />
        </EnrollmentModal>
      )}
    </section>
  );
}
